import { useLayoutEffect } from 'react'
import GeoJSON from 'geojson'
import { Expression, FillLayout, FillPaint, LineLayout, LinePaint, MapLayerMouseEvent, Point } from 'mapbox-gl'

import { useMapContext } from './MapContext'
import { isMapControlClick } from './mapControlClick'
import { safeAddLayer, safeRemoveLayer } from './mapTeardown'

export type LayerType = 'line' | 'fill'

interface MapGeoJsonLayerProps {
  types: LayerType[]
  id: string
  sourceId: string
  minZoom?: number
  maxZoom?: number
  layout?: LineLayout | FillLayout
  paint?: LinePaint | FillPaint
  filter?: unknown[]

  beforeLayerId?: string

  borderColor?: string | Expression
  borderWidth?: number | Expression
  borderOpacity?: number | Expression
  fillColor?: string | Expression
  fillOpacity?: number | Expression

  onBorderClick?: (features: GeoJSON.Feature[], point: Point) => void
  onFillClick?: (features: GeoJSON.Feature[], point: Point) => void
}

function MapGeoJsonLayer(props: MapGeoJsonLayerProps): null {
  const {
    types,
    id,
    sourceId,
    minZoom = 0,
    maxZoom = 22,
    layout,
    paint,
    filter,
    beforeLayerId,
    borderColor,
    borderWidth,
    borderOpacity,
    fillColor,
    fillOpacity,
    onBorderClick,
    onFillClick
  } = props

  const { map } = useMapContext()

  useLayoutEffect(() => {
    if (!map) return

    const borderClickListener = (event: MapLayerMouseEvent) => {
      if (isMapControlClick(map, event.point)) return
      onBorderClick?.(event.features ?? [], event.point)
    }

    const fillClickListener = (event: MapLayerMouseEvent) => {
      if (isMapControlClick(map, event.point)) return
      onFillClick?.(event.features ?? [], event.point)
    }

    const mouseEnterListener = () => {
      map.getCanvas().style.cursor = 'pointer'
    }

    const mouseLeaveListener = () => {
      map.getCanvas().style.cursor = ''
    }

    if (types.includes('line')) {
      safeAddLayer(
        map,
        {
          id: `${id}-line`,
          type: 'line',
          source: sourceId,
          minzoom: minZoom ?? 0,
          maxzoom: maxZoom ?? 0,
          ...(filter ? { filter: filter as never } : {}),
          layout: {
            ...layout
          },
          paint: {
            'line-color': borderColor ?? '#000000',
            'line-width': borderWidth ?? 1,
            'line-opacity': borderOpacity ?? 1,
            ...paint
          }
        },
        beforeLayerId
      )

      if (onBorderClick) {
        map.on('click', `${id}-line`, borderClickListener)
        map.on('mouseenter', `${id}-line`, mouseEnterListener)
        map.on('mouseleave', `${id}-line`, mouseLeaveListener)
      }
    }

    if (types.includes('fill')) {
      safeAddLayer(
        map,
        {
          id: `${id}-fill`,
          type: 'fill',
          source: sourceId,
          minzoom: minZoom,
          maxzoom: maxZoom,
          ...(filter ? { filter: filter as never } : {}),
          paint: {
            'fill-color': fillColor ?? '#000000',
            'fill-opacity': fillOpacity ?? 1
          }
        },
        beforeLayerId
      )

      if (onFillClick) {
        map.on('click', `${id}-fill`, fillClickListener)
        map.on('mouseenter', `${id}-fill`, mouseEnterListener)
        map.on('mouseleave', `${id}-fill`, mouseLeaveListener)
      }
    }

    return () => {
      if (types.includes('line')) safeRemoveLayer(map, `${id}-line`)
      if (types.includes('fill')) safeRemoveLayer(map, `${id}-fill`)
      if (onBorderClick) {
        map.off('click', `${id}-line`, borderClickListener)
        map.off('mouseenter', `${id}-line`, mouseEnterListener)
        map.off('mouseleave', `${id}-line`, mouseLeaveListener)
      }
      if (onFillClick) {
        map.off('click', `${id}-fill`, fillClickListener)
        map.off('mouseenter', `${id}-fill`, mouseEnterListener)
        map.off('mouseleave', `${id}-fill`, mouseLeaveListener)
      }
    }
  }, [map, onBorderClick, onFillClick])

  return null
}

export { MapGeoJsonLayer }
